import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
    AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { styled } from '@mui/material/styles';
import {
    offlineColumns,
    getNextStepsFormatter,
    sleepColumns,
    lowBatteryColumns,
} from 'store/services/monitor-portfolio';
import {
    ShipmentsDataTableEntry,
    SupplierStat,
    ConnectivitySummary,
    DeployedShippedSummary,
} from 'types';
import { useUi } from 'hooks/useUi';
import { DataGridPlaceholder } from 'components/DataGridPlaceholder/DataGridPlaceholder';
import MonitorPortfolioTable from 'views/Monitors/components/MonitorPortfolioTable/MonitorPortfolioTable';
import ConnectivitySummaryVisualization from '../../ConnectivitySummaryVisualization/ConnectivitySummaryVisualization';
import styles from './MonitorPortfolio.module.scss';
import { getDateWithTime } from 'helpers/dates';
import { TrackingLink } from '../../TrackingLink/TrackingLink';
import { DeployedShippedSummaryVisualization } from '../../DeployedShippedSummaryVisualization/DeployedShippedSummaryVisualization';
import { useEffect, useState } from 'react';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services';
import { useAuth } from 'hooks';
import { BatteryOfflineSummaryVisualization } from '../../BatteryOfflineSummaryVisualization/BatteryOfflineSummaryVisualization';
import { DataTableProps } from 'components/data/DataTable/DataTable';

export interface MonitorPortfolioProps {
    /** Connectivity information for the supplier's deployed devices. */
    connectivitySummary: ConnectivitySummary;
    /** Quantity information for the supplier's deployed vs shipped devices. */
    deployedShippedSummary: DeployedShippedSummary;
    /** Short device id mapped to the corresponding shipment data. */
    deviceIdToShipmentDataTable: Record<string, ShipmentsDataTableEntry>;
    /** Supplier's stats for devices connectivity and status. */
    supplierStats: SupplierStat;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

/** The Monitor Portfolio Overview page. */
export default function MonitorPortfolio({
    connectivitySummary,
    deployedShippedSummary,
    deviceIdToShipmentDataTable,
    supplierStats,
    viewAsSupplier,
}: MonitorPortfolioProps) {
    const { ui } = useUi();
    const { user } = useAuth();
    const [query] = useLazyGetSupplierDevicesEsQuery();
    const [numberOfLowBatteryDevices, setNumberOfLowBatteryDevices] =
        useState<number>(0);
    const [numberOfflineLowBatteryDevices, setNumberOfflineLowBatteryDevices] =
        useState<number>(0);
    const [numberOnlineLowBatteryDevices, setNumberOnlineLowBatteryDevices] =
        useState<number>(0);
    const [accordionsExpanded, setAccordionsExpanded] = useState([
        false,
        false,
        false,
        false,
    ]);

    const toggleAccordion = (whichAccordion: number) => {
        const tempAccordionsExpanded = [...accordionsExpanded];
        tempAccordionsExpanded[whichAccordion] =
            !tempAccordionsExpanded[whichAccordion];
        setAccordionsExpanded(tempAccordionsExpanded);
    };

    useEffect(() => {
        if (user?.uid && user?.protected) {
            const supplierId = viewAsSupplier ?? user.protected.supplierId;
            // Get total number of devices with low battery.
            query({
                userUid: user.uid,
                supplierId,
                queryConfig: {
                    limit: 1,
                    lowBatteryOnly: true,
                    statusFilter: 'deployed',
                    excludeVirtualDevices: true,
                    offlineOnly: false,
                },
            }).then((res) => {
                if (res.isSuccess) setNumberOfLowBatteryDevices(res.data.total);
            });

            // Get total number of devices with low battery that are offline.
            query({
                userUid: user.uid,
                supplierId,
                queryConfig: {
                    limit: 1,
                    lowBatteryOnly: true,
                    statusFilter: 'deployed',
                    excludeVirtualDevices: true,
                    offlineOnly: true,
                },
            }).then((res) => {
                if (res.isSuccess)
                    setNumberOfflineLowBatteryDevices(res.data.total);
            });
        }
    }, [query, user?.protected, user?.uid, viewAsSupplier]);

    // Calculate the number of online devices with low battery.
    useEffect(() => {
        setNumberOnlineLowBatteryDevices(
            numberOfLowBatteryDevices - numberOfflineLowBatteryDevices
        );
    }, [numberOfLowBatteryDevices, numberOfflineLowBatteryDevices]);

    const Accordion = styled((props: AccordionProps) => (
        <MuiAccordion disableGutters elevation={0} square {...props} />
    ))(({ theme }) => ({
        border: `1px solid ${theme.palette.divider}`,
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&:before': {
            display: 'none',
        },
    }));

    const AccordionSummary = styled((props: AccordionSummaryProps) => (
        <MuiAccordionSummary
            expandIcon={
                <ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />
            }
            {...props}
        />
    ))(({ theme }) => ({
        backgroundColor:
            theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, .05)'
                : 'rgba(0, 0, 0, .03)',
        flexDirection: 'row-reverse',
        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
        },
        '& .MuiAccordionSummary-content': {
            marginLeft: theme.spacing(1),
        },
    }));

    /** Columns for data grid table showing shipped devices. */
    const shippedColumns: DataTableProps['columns'] = [
        {
            field: 'orderDate',
            headerName: 'Ordered',
            valueGetter: (params) => {
                if (
                    params &&
                    deviceIdToShipmentDataTable &&
                    deviceIdToShipmentDataTable[params.row.shortDeviceId]
                ) {
                    const timestamp =
                        deviceIdToShipmentDataTable[params.row.shortDeviceId]
                            .shipTime;
                    return timestamp ? getDateWithTime(timestamp) : '';
                }
            },
            flex: 1,
        },
        {
            field: 'shortDeviceId',
            headerName: 'Device ID',
            flex: 1.25,
        },
        {
            field: 'trackingLink',
            headerName: 'Tracking',
            flex: 2,
            renderCell: (params) => {
                if (
                    params &&
                    deviceIdToShipmentDataTable &&
                    deviceIdToShipmentDataTable[params.row.shortDeviceId]
                ) {
                    const trackingLink =
                        deviceIdToShipmentDataTable[params.row.shortDeviceId]
                            .trackingLink;
                    const deliveryCarrier =
                        deviceIdToShipmentDataTable[params.row.shortDeviceId]
                            .deliveryCarrier;
                    return (
                        <TrackingLink
                            deliveryCarrier={deliveryCarrier}
                            trackingLink={trackingLink}
                        />
                    );
                }
            },
        },
        {
            field: 'status',
            headerName: 'Next Steps',
            valueFormatter: getNextStepsFormatter,
        },
    ];

    return (
        <div
            style={{ height: '100vh' }}
            className={styles.MonitorPortfolio}
            data-testid="MonitorPortfolio"
        >
            <Accordion
                data-testid="MonitorPortfolioDeployedTable"
                defaultExpanded={false}
                expanded={accordionsExpanded[0]}
                onChange={() => toggleAccordion(0)}
            >
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <div className={styles.labelContainer}>
                        <label aria-label="Offline Monitors Table">
                            Offline Monitors{' '}
                            <span>({supplierStats.offlineCount ?? 0})</span>{' '}
                        </label>
                        <ConnectivitySummaryVisualization
                            connectivitySummary={connectivitySummary}
                        />
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    {ui.animatingViewTransition || !accordionsExpanded[0] ? (
                        <DataGridPlaceholder />
                    ) : (
                        <MonitorPortfolioTable
                            columns={offlineColumns}
                            tableName={'offlineMonitors'}
                            statusFilter="deployed"
                            viewAsSupplier={viewAsSupplier}
                            offlineOnly={true}
                        />
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion
                data-testid="MonitorPortfolioLowBatteryTable"
                defaultExpanded={false}
                expanded={accordionsExpanded[1]}
                onChange={() => toggleAccordion(1)}
            >
                <AccordionSummary
                    aria-controls="panel2a-content"
                    id="panel2a-header"
                >
                    <div className={styles.labelContainer}>
                        <label aria-label="Low Battery Monitors Table">
                            Low Battery Monitors{' '}
                            <span>({numberOfLowBatteryDevices})</span>
                        </label>
                        <BatteryOfflineSummaryVisualization
                            onlineCount={numberOnlineLowBatteryDevices}
                            offlineCount={numberOfflineLowBatteryDevices}
                        />
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    {ui.animatingViewTransition || !accordionsExpanded[1] ? (
                        <DataGridPlaceholder />
                    ) : (
                        <MonitorPortfolioTable
                            columns={lowBatteryColumns}
                            tableName={'lowBatteryMonitors'}
                            statusFilter="deployed"
                            viewAsSupplier={viewAsSupplier}
                            lowBatteryOnly={true}
                        />
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion
                data-testid="MonitorPortfolioShippedTable"
                defaultExpanded={false}
                expanded={accordionsExpanded[2]}
                onChange={() => toggleAccordion(2)}
            >
                <AccordionSummary
                    aria-controls="panel3a-content"
                    id="panel3a-header"
                >
                    <div className={styles.labelContainer}>
                        <label aria-label="Shipped Monitors Table">
                            Shipped Monitors{' '}
                            <span>({supplierStats?.status?.shipped ?? 0})</span>
                        </label>
                        <DeployedShippedSummaryVisualization
                            deployedShippedSummary={deployedShippedSummary}
                        />
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    {ui.animatingViewTransition || !accordionsExpanded[2] ? (
                        <DataGridPlaceholder />
                    ) : (
                        <MonitorPortfolioTable
                            columns={shippedColumns}
                            tableName={'shippedMonitors'}
                            statusFilter="shipped"
                            viewAsSupplier={viewAsSupplier}
                        ></MonitorPortfolioTable>
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion
                data-testid="MonitorPortfolioSleepTable"
                defaultExpanded={false}
                expanded={accordionsExpanded[3]}
                onChange={() => toggleAccordion(3)}
            >
                <AccordionSummary
                    aria-controls="panel4a-content"
                    id="panel4a-header"
                >
                    <label aria-label="Shipped Monitors Table">
                        Sleeping Monitors{' '}
                        <span>({supplierStats?.status?.sleep ?? 0})</span>
                    </label>
                </AccordionSummary>
                <AccordionDetails>
                    {ui.animatingViewTransition || !accordionsExpanded[3] ? (
                        <DataGridPlaceholder />
                    ) : (
                        <MonitorPortfolioTable
                            columns={sleepColumns}
                            tableName={'sleepingMonitors'}
                            statusFilter="sleep"
                            viewAsSupplier={viewAsSupplier}
                        ></MonitorPortfolioTable>
                    )}
                </AccordionDetails>
            </Accordion>
        </div>
    );
}
